import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, ArrowRight, X, Save, ChevronDown, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAccessibleLocations } from '@/hooks/useAccessibleLocations';
import { authFetch } from '@/services/api.service';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Combobox con busqueda para ingredientes
const IngredientCombobox: React.FC<{
  value: string;
  displayName: string;
  ingredients: ApiIngredient[];
  excludeIds: Set<string>;
  onChange: (id: string) => void;
}> = ({ value, displayName, ingredients, excludeIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = ingredients.filter(ing => {
    if (excludeIds.has(String(ing.id)) && String(ing.id) !== value) return false;
    if (!search) return true;
    return ing.name.toLowerCase().includes(search.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full h-8 px-3 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-left"
      >
        <span className={displayName ? 'text-gray-900 truncate' : 'text-gray-500 truncate'}>
          {displayName || 'Seleccionar...'}
        </span>
        <ChevronDown className="w-3 h-3 ml-1 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar ingrediente..."
                className="w-full h-8 pl-7 pr-2 text-sm border border-gray-200 rounded-md outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
            ) : (
              filtered.map((ing) => (
                <button
                  key={ing.id}
                  type="button"
                  onClick={() => {
                    onChange(String(ing.id));
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${
                    String(ing.id) === value ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                  }`}
                >
                  {ing.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface ApiIngredient {
  id: number;
  name: string;
  unitId: number;
  unit?: { id: number; name: string; symbol: string };
}

interface ManualTransferItem {
  id: string;
  ingredientId: string;
  ingredientName: string;
  requestedQuantity: number;
  unit: string;
  reason: string;
  stock: number;
}

interface ManualTransferPageProps {
  onCreateTransfer?: (transferData: any) => Promise<{ success: boolean; error?: string }>;
  loading?: boolean;
}

export const ManualTransferPage: React.FC<ManualTransferPageProps> = ({
  onCreateTransfer,
  loading: externalLoading
}) => {
  const { locations } = useAccessibleLocations();
  const [ingredients, setIngredients] = useState<ApiIngredient[]>([]);
  const [formData, setFormData] = useState({
    fromLocationId: '',
    toLocationId: '',
    notes: '',
  });
  const [items, setItems] = useState<ManualTransferItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar ingredientes desde la API
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/ingredients?limit=500`);
        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          if (Array.isArray(data)) {
            setIngredients(data);
          }
        }
      } catch (err) {
        console.error('Error fetching ingredients:', err);
      }
    };
    fetchIngredients();
  }, []);

  // Cargar stock del ingrediente en el local origen
  const fetchStock = useCallback(async (ingredientId: string, locationId: string): Promise<number> => {
    if (!ingredientId || !locationId) return 0;
    try {
      const response = await authFetch(`${API_BASE_URL}/locations/${locationId}/inventory`);
      if (response.ok) {
        const result = await response.json();
        const inventoryData = result.data || result;
        if (Array.isArray(inventoryData)) {
          const item = inventoryData.find((inv: any) => String(inv.ingredientId) === String(ingredientId));
          return item ? parseFloat(item.currentStock) || 0 : 0;
        }
      }
    } catch (err) {
      console.error('Error fetching stock:', err);
    }
    return 0;
  }, []);

  const toLocations = locations.filter(loc => String(loc.id) !== formData.fromLocationId);

  const handleAddItem = () => {
    setItems(prev => [...prev, {
      id: `item-${Date.now()}`,
      ingredientId: '',
      ingredientName: '',
      requestedQuantity: 0,
      unit: 'und',
      reason: '',
      stock: 0,
    }]);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleItemChange = async (itemId: string, field: keyof ManualTransferItem, value: any) => {
    if (field === 'ingredientId') {
      const ingredient = ingredients.find(ing => String(ing.id) === value);
      const stock = await fetchStock(value, formData.fromLocationId);
      setItems(prev => prev.map(item =>
        item.id === itemId
          ? {
              ...item,
              ingredientId: value,
              ingredientName: ingredient?.name || '',
              unit: ingredient?.unit?.symbol || 'und',
              stock,
            }
          : item
      ));
    } else {
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      ));
    }
  };

  // Cuando cambia el local origen, recargar stock de todos los items
  useEffect(() => {
    if (!formData.fromLocationId || items.length === 0) return;
    const updateStocks = async () => {
      const updated = await Promise.all(
        items.map(async (item) => {
          if (!item.ingredientId) return item;
          const stock = await fetchStock(item.ingredientId, formData.fromLocationId);
          return { ...item, stock };
        })
      );
      setItems(updated);
    };
    updateStocks();
    // Solo cuando cambia fromLocationId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fromLocationId]);

  const handleSubmit = async () => {
    if (!formData.fromLocationId || !formData.toLocationId || items.length === 0) return;

    const validItems = items.filter(item => item.ingredientId && item.requestedQuantity > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un ingrediente con cantidad mayor a 0');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onCreateTransfer?.({
        fromLocationId: formData.fromLocationId,
        toLocationId: formData.toLocationId,
        type: 'transfer',
        notes: formData.notes,
        items: validItems.map(item => ({
          ingredientId: item.ingredientId,
          requestedQuantity: item.requestedQuantity,
          reason: item.reason || 'Transferencia manual',
        })),
      });

      if (result?.success) {
        toast.success('Transferencia creada exitosamente');
        setFormData({ fromLocationId: '', toLocationId: '', notes: '' });
        setItems([]);
      } else {
        toast.error(result?.error || 'Error al crear transferencia');
      }
    } catch (error) {
      toast.error('Error al crear transferencia');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.fromLocationId && formData.toLocationId && items.length > 0 &&
    items.some(item => item.ingredientId && item.requestedQuantity > 0);

  // Ingredientes ya seleccionados (para excluirlos del dropdown)
  const selectedIngredientIds = new Set(items.map(i => i.ingredientId).filter(Boolean));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-macartur-primary" />
            Crear Transferencia Manual
          </CardTitle>
          <CardDescription>
            Crea transferencias puntuales entre ubicaciones para casos especificos o emergencias
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Location Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-sm font-medium text-gray-700">Origen:</Label>
              <Select value={formData.fromLocationId} onValueChange={(value) => setFormData(prev => ({ ...prev, fromLocationId: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar ubicacion origen" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-center">
              <ArrowRight className="w-6 h-6 text-gray-400" />
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Destino:</Label>
              <Select value={formData.toLocationId} onValueChange={(value) => setFormData(prev => ({ ...prev, toLocationId: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar ubicacion destino" />
                </SelectTrigger>
                <SelectContent>
                  {toLocations.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-lg font-medium text-gray-900">Ingredientes a Transferir</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddItem}
                disabled={!formData.fromLocationId || !formData.toLocationId}
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Ingrediente
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-500">
                  {!formData.fromLocationId || !formData.toLocationId
                    ? 'Selecciona ubicaciones de origen y destino para agregar ingredientes'
                    : 'No hay ingredientes agregados. Haz clic en "Agregar Ingrediente" para comenzar.'
                  }
                </p>
              </div>
            ) : (
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  {/* Table Headers */}
                  <div className="grid gap-3 pb-2 mb-3 border-b border-gray-200" style={{ gridTemplateColumns: '40px 2fr 80px 100px 1.5fr 60px' }}>
                    <div className="text-xs font-medium text-gray-600">#</div>
                    <div className="text-xs font-medium text-gray-600">Ingrediente</div>
                    <div className="text-xs font-medium text-gray-600">Stock</div>
                    <div className="text-xs font-medium text-gray-600">Cantidad</div>
                    <div className="text-xs font-medium text-gray-600">Motivo</div>
                    <div className="text-xs font-medium text-gray-600 text-center">Accion</div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <div key={item.id} className="grid gap-3 items-center py-1" style={{ gridTemplateColumns: '40px 2fr 80px 100px 1.5fr 60px' }}>
                        <div>
                          <Badge variant="outline" className="text-xs">
                            #{index + 1}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <IngredientCombobox
                              value={item.ingredientId}
                              displayName={item.ingredientName}
                              ingredients={ingredients}
                              excludeIds={selectedIngredientIds}
                              onChange={(val) => handleItemChange(item.id, 'ingredientId', val)}
                            />
                          </div>
                          <span className="text-xs text-gray-500 font-medium px-2 py-1 bg-gray-100 rounded">
                            {item.unit}
                          </span>
                        </div>

                        <div>
                          <div className={`h-8 px-2 bg-gray-50 border border-gray-200 rounded-md flex items-center text-sm ${item.stock <= 0 ? 'text-red-600' : 'text-gray-700'}`}>
                            {item.stock}
                          </div>
                        </div>

                        <div>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.requestedQuantity || ''}
                            onChange={(e) => handleItemChange(item.id, 'requestedQuantity', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <Input
                            value={item.reason}
                            onChange={(e) => handleItemChange(item.id, 'reason', e.target.value)}
                            className="h-8 text-sm"
                            placeholder=""
                          />
                        </div>

                        <div className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Notas adicionales (opcional):</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="mt-1"
              placeholder="Informacion adicional sobre esta transferencia..."
              rows={3}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setFormData({ fromLocationId: '', toLocationId: '', notes: '' });
                setItems([]);
              }}
            >
              Limpiar Formulario
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting || externalLoading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Creando...' : 'Crear Transferencia'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};